import React, { useState, useEffect, useRef } from "react";
import { db } from "../../../backend/firebaseConfig";
import {
  doc,
  onSnapshot,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  ref as storageRef,
  listAll,
  deleteObject,
} from "firebase/storage";
import Modal from "react-modal";
import ViewInsurance from "../../../components/UI/organisms/insurance/ViewInsurance";
import DentalChart from "../../../components/UI/organisms/chart/DentalChart";
import MedicalHistory from "../../../components/UI/organisms/history/MedicalHistory";
import PersonnelTreatmentHistoryModal from "../../../components/UI/organisms/history/PersonnelTreatmentHistoryModal";

// Import Atoms
import SplashScreen from "../../../components/UI/atoms/splash/SplashScreen";
import Icon from "../../../components/UI/atoms/icon/Icon";

// Imports MUI Components
import {
  Box,
  Button,
  Stack,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
  Slide,
  useMediaQuery,
  Tooltip,
  Avatar
} from "@mui/material";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import { Pagination } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

// Import Assets
import user from "../../../assets/user.png";

// Import Organisms
import Sidebar from "../../../components/UI/organisms/sidebar/Sidebar";

// Import Functions
import { fetchUserDetails } from "../../../components/functions/fetchUserDetails";
import { notifyFirstUser } from "../../../components/functions/notifyFirstUser";
import { addNotification } from "../../../components/functions/notifyComp";
import Header from "../../../components/UI/organisms/header/logged-in/HeaderLoggedIn";
import { storage } from "../../../backend/firebaseConfig";
import { rowGap } from "@mui/system";
import Text from "../../../components/UI/atoms/text/Text";
import Search from "../../../components/UI/molecules/search/Search";


Modal.setAppElement("#root");

function formatDateMMDDYYYY(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

function formatContactInfo(userDetails) {
  if (!userDetails) return "";

  // If both email and contact number are provided, show both (email first, then phone)
  if (userDetails.email && userDetails.contactNumber) {
    const contact = userDetails.contactNumber.toString();
    const formattedContact =
      contact.length === 11 && contact.startsWith("09")
        ? `${contact.slice(0, 4)}-${contact.slice(4, 7)}-${contact.slice(7)}`
        : contact;
    return (
      <div>
        <div>{userDetails.email}</div>
        <div style={{ color: "#666", fontSize: "12px", marginTop: "2px" }}>
          {formattedContact}
        </div>
      </div>
    );
  }

  // If only email is provided, show email
  if (userDetails.email) {
    return userDetails.email;
  }

  // If only contact number is provided, format it in Philippine format
  if (userDetails.contactNumber) {
    const contact = userDetails.contactNumber.toString();
    // Convert 09XXXXXXXXX to 0962-233-9466 format
    if (contact.length === 11 && contact.startsWith("09")) {
      return `${contact.slice(0, 4)}-${contact.slice(4, 7)}-${contact.slice(
        7
      )}`;
    }
    return contact; // Return as-is if format is unexpected
  }

  return "No contact info";
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const extractTimePart = (timeStr = "", index = 0) => {
  if (!timeStr) return "";
  const parts = timeStr.split("-");
  if (parts.length > index) {
    return parts[index].trim();
  }
  return timeStr.trim();
};

function parseTimeTo24Hour(timeStr = "") {
  const extracted = extractTimePart(timeStr);
  const match = extracted.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);

  if (!match) {
    return { hours: 0, minutes: 0 };
  }

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const meridiem = match[3] ? match[3].toUpperCase() : null;

  if (meridiem === "PM" && hours !== 12) {
    hours += 12;
  }

  if (meridiem === "AM" && hours === 12) {
    hours = 0;
  }

  return {
    hours: Number.isNaN(hours) ? 0 : hours,
    minutes: Number.isNaN(minutes) ? 0 : minutes,
  };
}

const deleteAllTreatmentImages = async (patientUid) => {
  if (!patientUid) return;

  const rootRef = storageRef(storage, `treatment-images/${patientUid}`);

  const deleteFolderRecursive = async (prefixRef) => {
    const { prefixes = [], items = [] } = await listAll(prefixRef);

    for (const item of items) {
      try {
        await deleteObject(item);
      } catch (error) {
        console.error("Error deleting storage object:", item.fullPath, error);
      }
    }

    for (const folderRef of prefixes) {
      await deleteFolderRecursive(folderRef);
    }
  };

  try {
    await deleteFolderRecursive(rootRef);
  } catch (error) {
    console.error("Error deleting treatment images for patient:", patientUid, error);
  }
};

const deletePatientAppointmentImages = async (patientUid) => {
  if (!patientUid) return;

  try {
    const appointmentsSnapshot = await getDocs(collection(db, "appointments"));
    const deletePromises = [];

    appointmentsSnapshot.forEach((docSnap) => {
      const appointmentsData = docSnap.data();
      if (!appointmentsData) return;

      Object.values(appointmentsData).forEach((appointment) => {
        if (!appointment) return;

        const appointmentPatientUid =
          appointment.patientId || appointment.uid || appointment.userId;

        if (appointmentPatientUid !== patientUid) return;

        if (Array.isArray(appointment.images)) {
          appointment.images.forEach((imageRef) => {
            const imagePath = imageRef?.path || imageRef?.storagePath;
            if (!imagePath) return;

            const fileRef = storageRef(storage, imagePath);
            deletePromises.push(
              deleteObject(fileRef).catch((error) => {
                console.error(
                  "Failed to delete treatment image:",
                  imagePath,
                  error
                );
              })
            );
          });
        }
      });
    });

    await Promise.all(deletePromises);
  } catch (error) {
    console.error(
      "Error removing patient treatment images from appointments:",
      error
    );
  }
};

function formatTimeDisplay(timeStr = "") {
  const extracted = extractTimePart(timeStr);
  if (!extracted) {
    return "N/A";
  }

  const match = extracted.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) {
    return extracted;
  }

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const meridiem = match[3] ? match[3].toUpperCase() : null;

  if (!meridiem) {
    const period = hours >= 12 ? "PM" : "AM";
    let adjustedHours = hours;
    if (hours === 0) {
      adjustedHours = 12;
    } else if (hours > 12) {
      adjustedHours = hours - 12;
    }
    return `${adjustedHours}:${minutes} ${period}`;
  }

  if (hours === 0 || hours === 12) {
    hours = 12;
  } else if (hours > 12) {
    hours = hours % 12;
  }

  return `${hours}:${minutes} ${meridiem}`;
}

function formatDateLong(dateStr = "") {
  if (!dateStr) return "N/A";
  const [yearStr, monthStr, dayStr] = dateStr.split("-");

  if (!yearStr || !monthStr || !dayStr) {
    return dateStr;
  }

  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);

  if (
    Number.isNaN(year) ||
    Number.isNaN(monthIndex) ||
    Number.isNaN(day) ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    return dateStr;
  }

  return `${MONTH_NAMES[monthIndex]} ${String(day).padStart(2, "0")}, ${year}`;
}

function createAppointmentDate(dateStr = "", timeStr = "") {
  const [yearStr, monthStr, dayStr] = dateStr.split("-");
  if (!yearStr || !monthStr || !dayStr) {
    return null;
  }

  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);

  if (
    Number.isNaN(year) ||
    Number.isNaN(monthIndex) ||
    Number.isNaN(day) ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    return null;
  }

  const { hours, minutes } = parseTimeTo24Hour(timeStr);

  return new Date(year, monthIndex, day, hours, minutes);
}

const PersonnelPatientRecord = () => {
  // State variables
  const [appointments, setAppointments] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientRecords, setPatientRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [patientsPage, setPatientsPage] = useState(1);
  const patientsPerPage = 10;
  useEffect(() => {
    setPatientsPage(1);
  }, [searchTerm, patients, filteredPatients]);
  // compute paginated slice
  const totalPatientPages = Math.max(
    1,
    Math.ceil(filteredPatients.length / patientsPerPage)
  );
  const startPatient = (patientsPage - 1) * patientsPerPage;
  const endPatient = startPatient + patientsPerPage;
  const currentPatients = filteredPatients.slice(startPatient, endPatient);
  const [insuranceDetails, setInsuranceDetails] = useState(null);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const medicalHistoryRef = useRef(null);
  const [selectedPatientName, setSelectedPatientName] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
  });
  const [selectedPatientInfo, setSelectedPatientInfo] = useState(null);
  const [showTreatmentHistory, setShowTreatmentHistory] = useState(false);
  const [showDentalChart, setShowDentalChart] = useState(false);
  const [activeTab, setActiveTab] = useState("personal"); // 'personal' or 'medical'
  const [userDetails, setUserDetails] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [patientToRemove, setPatientToRemove] = useState(null);
  const [isRemovingPatient, setIsRemovingPatient] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  
  // Update Password Modal State
  const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({ newPassword: "", confirmPassword: "" });

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertImage, ] = useState("");
  const [alertButton, ] = useState("");
  const [alertButtonType, ] = useState("false");
  const [alertLink, ] = useState("");
const [showSplash, setShowSplash] = useState(true);

  const [showTooltip, setShowTooltip] = React.useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  React.useEffect(() => {
    if (isSmallScreen) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false); 
    }
  }, [isSmallScreen]);

  const handleTooltipClose = () => {
    setShowTooltip(false);
  };

  const auth = getAuth();

  // Store unsubscribe functions for manual cleanup
  const [, setUnsubscribeFunctions] = useState([]);

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    const notify = notifyFirstUser();
    return () => {
      details && details();
      notify && notify();
    };
  }, []);

  // Fetch all appointments
  useEffect(() => {
    let unsubscribeAppointments = null;
    let unsubscribePatients = null;

    unsubscribeAppointments = fetchAllAppointments();
    unsubscribePatients = fetchAllPatients();

    // Store unsubscribe functions for manual cleanup
    setUnsubscribeFunctions([unsubscribeAppointments, unsubscribePatients]);

    return () => {
      if (unsubscribeAppointments) {
        unsubscribeAppointments();
      }
      if (unsubscribePatients) {
        unsubscribePatients();
      }
    };
  }, []);

  // Fetch all appointments
  const fetchAllAppointments = () => {
    const appointmentsRef = collection(db, "appointments");
    return onSnapshot(
      appointmentsRef,
      (snapshot) => {
        let allAppointmentsList = [];
        snapshot.forEach((doc) => {
          const appointmentObj = doc.data();
          // If the appointment is nested, extract each appointment
          if (typeof appointmentObj === "object" && appointmentObj !== null) {
            Object.entries(appointmentObj).forEach(([id, value]) => {
              allAppointmentsList.push({ id, ...value });
            });
          }
        });
        setAppointments(allAppointmentsList);
      },
      (error) => {
        // Silently handle permission errors during sign-out
        if (error.code === "permission-denied") {
          setAppointments([]);
          return;
        }
        console.error("Error fetching appointments:", error);
      }
    );
  };

  // Fetch all patient users
  const fetchAllPatients = () => {
    const usersRef = collection(db, "users");
    return onSnapshot(
      usersRef,
      (snapshot) => {
        let patientList = [];
        snapshot.forEach((doc) => {
          const user = doc.data();
          // Only include active patients (not deleted or anonymized)
          if ((user.userType === "Patient" || user.role === "Patient") && 
              user.accountDeleted !== true && 
              user.accountStatus !== 'anonymized') {
            patientList.push({
              email: user.email || null,
              contactNumber: user.contactNumber || null,
              firstName: user.firstName,
              middleName: user.middleName,
              lastName: user.lastName,
              birthDate: user.birthDate,
              age: user.age,
              address: user.address,
              civilStatus: user.civilStatus,
              uid: user.uid,
              profilePictureUrl: user.profilePictureUrl,
            });
          }
        });
        setPatients(patientList);
        setFilteredPatients(patientList);
      },
      (error) => {
        // Silently handle permission errors during sign-out
        if (error.code === "permission-denied") {
          setPatients([]);
          setFilteredPatients([]);
          return;
        }
        console.error("Error fetching patients:", error);
      }
    );
  };

  // Fetch user details for sidebar profile (search all personnel types)
  useEffect(() => {
    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          setUserDetails(snapshot.data());
        }
      }
      setTimeout(() => setShowSplash(false), 1200);
    });
    return () => {
      authUnsubscribe();
    };
  }, [auth]);

  // Function to handle patient selection
  const handlePatientClick = (patientUid) => {
    // Find patient by uid (more reliable than email)
    const patient = patients.find((patient) => patient.uid === patientUid);
    if (!patient) return;

    setSelectedPatient(patient.uid);
    setSelectedPatientName({
      firstName: patient.firstName,
      middleName: patient.middleName,
      lastName: patient.lastName,
    });
    setSelectedPatientInfo(patient);
    // Filter appointments by unique patient uid only
    const patientAppointments = appointments.filter(
      (appointment) =>
        (appointment.patientId === patient.uid ||
          appointment.uid === patient.uid) &&
        appointment.status === "Completed"
    );
    
    setPatientRecords(patientAppointments);
    setActiveTab("personal"); // Reset tab when selecting a new patient
  };

  // Function to handle search input
  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase().trim();
    setSearchTerm(term);
    const filtered = patients.filter((patient) => {
      // Use patient.uid for appointment filtering
      const patientAppointments = appointments.filter(
        (appointment) =>
          (appointment.patientId === patient.uid ||
            appointment.uid === patient.uid) &&
          appointment.status === "Completed"
      );
      
      // Create full name string by combining first, middle, and last name
      const firstName = (patient.firstName || "").toLowerCase().trim();
      const middleName = (patient.middleName || "").toLowerCase().trim();
      const lastName = (patient.lastName || "").toLowerCase().trim();
      const fullName = `${firstName} ${middleName} ${lastName}`.trim().replace(/\s+/g, " ");
      
      // Check if search term matches individual fields
      const matchesIndividualFields = 
        (patient.email && patient.email.toLowerCase().includes(term)) ||
        (patient.contactNumber &&
          patient.contactNumber.toString().toLowerCase().includes(term)) ||
        firstName.includes(term) ||
        middleName.includes(term) ||
        lastName.includes(term);
      
      // Check if search term matches full name
      const matchesFullName = fullName.includes(term);
      
      // Split search term into words and check if all words match any part of the name
      const searchWords = term.split(/\s+/).filter(word => word.length > 0);
      const matchesAllWords = searchWords.length > 0 && searchWords.every(word =>
        firstName.includes(word) ||
        middleName.includes(word) ||
        lastName.includes(word) ||
        fullName.includes(word)
      );
      
      // Check appointment-related fields
      const matchesAppointments = patientAppointments.some(
        (appointment) =>
          (appointment.date &&
            typeof appointment.date === "string" &&
            appointment.date.toLowerCase().includes(term)) ||
          (appointment.services &&
            Array.isArray(appointment.services) &&
            appointment.services.some(
              (service) =>
                service &&
                typeof service === "string" &&
                service.toLowerCase().includes(term)
            ))
      );
      
      return matchesIndividualFields || matchesFullName || matchesAllWords || matchesAppointments;
    });
    setFilteredPatients(filtered);
  };

  // Function to handle viewing insurance details
  const handleViewInsuranceDetails = (appointment) => {
    setInsuranceDetails(appointment.insuranceDetails);
    setShowInsuranceForm(true);
    setShowTreatmentHistory(false);
  };

  // Function to close the insurance modal
  const handleInsuranceClose = () => {
    setShowInsuranceForm(false);
    setInsuranceDetails(null);
    setShowTreatmentHistory(true);
  };

  // Function to close the treatment history modal
  const handleTreatmentHistoryClose = () => {
    setShowTreatmentHistory(false);
  };

  // Function to close the dental chart modal
  const handleDentalChartClose = () => {
    setShowDentalChart(false);
  };

  // Handler to update dentistRemarks and images in patientRecords when saved in modal
  const handleRemarksSaved = ({ id, date, dentistRemarks, images }) => {
    setPatientRecords((prev) =>
      prev.map((appt) =>
        appt.id === id && appt.date === date
          ? { ...appt, dentistRemarks, images }
          : appt
      )
    );
  };

  const formatPatientFullName = (patient) => {
    if (!patient) return "";
    const middle =
      patient.middleName && patient.middleName !== "--"
        ? `${patient.middleName} `
        : "";

    return `${patient.firstName || ""} ${middle}${patient.lastName || ""}`
      .trim()
      .replace(/\s+/g, " ");
  };

const SlideDownTransition = (props) => <Slide {...props} direction="down" />;

  // Function to handle remove patient button click
  const handleRemovePatient = (patient) => {
    setPatientToRemove(patient);
    setShowRemoveModal(true);
  };

  // Function to confirm patient removal
  const confirmRemovePatient = async () => {
    if (!patientToRemove) return;

    setIsRemovingPatient(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found.");
      }

      const currentUserRef = doc(db, "users", currentUser.uid);
      const currentUserSnap = await getDoc(currentUserRef);
      const currentUserData = currentUserSnap.data() || {};

      const role = currentUserData.role || currentUserData.userType;
      const personnelName = `${currentUserData.firstName || ""} ${
        currentUserData.lastName || ""
      }`
        .trim()
        .replace(/\s+/g, " ");

      let notificationActor = "A clinic personnel";

      if (role === "DentistOwner" || role === "AssociateDentist") {
        notificationActor = personnelName
          ? `Dr. ${personnelName}`
          : "Dr. (Clinic Personnel)";
      } else if (role === "ClinicStaff") {
        const gender = (currentUserData.gender || "").toLowerCase();
        let prefix = "Mr./Ms.";

        if (gender === "male") {
          prefix = "Mr.";
        } else if (gender === "female") {
          prefix = "Ms.";
        }

        notificationActor = personnelName
          ? `${prefix} ${personnelName}`
          : `${prefix} (Clinic Personnel)`;
      } else if (personnelName) {
        notificationActor = personnelName;
      }

      const patientName = formatPatientFullName(patientToRemove);

      const now = new Date();
      const upcomingAppointments = appointments.filter((appointment) => {
        const appointmentPatientUid =
          appointment.uid || appointment.userId || appointment.patientId;

        if (appointmentPatientUid !== patientToRemove.uid) {
          return false;
        }

        const status = (appointment.status || "").toLowerCase();
        if (status !== "pending" && status !== "confirmed") {
          return false;
        }

        const appointmentDateValue =
          appointment.date ||
          appointment.appointmentDate ||
          appointment.appointment_date;

        if (!appointmentDateValue) {
          return false;
        }

        const appointmentDateTime = createAppointmentDate(
          appointmentDateValue,
          appointment.startTime || appointment.time
        );

        if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
          return false;
        }

        return appointmentDateTime >= now;
      });

      const apiUrl = process.env.REACT_APP_API_URL || "";
      const response = await fetch(
        `${apiUrl}/api/delete-user?uid=${patientToRemove.uid}&action=disable`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to disable patient account");
      }

      const usersRef = collection(db, "users");
      const personnelRoles = ["DentistOwner", "AssociateDentist", "ClinicStaff"];
      const usersSnapshot = await getDocs(usersRef);
      const clinicPersonnel = [];

      usersSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        const userRole = userData.role || userData.userType;

        if (personnelRoles.includes(userRole)) {
          clinicPersonnel.push({
            uid: userDoc.id,
            role: userRole,
            ...userData,
          });
        }
      });

      let notificationMessage = "";
      if (role === "DentistOwner" || role === "AssociateDentist") {
        notificationMessage = `${notificationActor} removed the patient ${patientName || "N/A"} from the clinic records.`;
      } else if (role === "ClinicStaff") {
        notificationMessage = `${notificationActor} has removed the patient ${patientName || "N/A"} from the clinic records.`;
      } else {
        notificationMessage = `${notificationActor} removed the patient ${patientName || "N/A"} from the clinic records.`;
      }

      for (const personnel of clinicPersonnel) {
        try {
          await addNotification(personnel.uid, notificationMessage);
        } catch (notificationError) {
          console.error(
            `Failed to send notification to ${personnel.uid}:`,
            notificationError
          );
        }
      }

      for (const appointment of upcomingAppointments) {
        if (!appointment.dentistId) {
          continue;
        }

        const appointmentDateValue =
          appointment.date ||
          appointment.appointmentDate ||
          appointment.appointment_date;

        const startTimeRaw =
          appointment.startTime || extractTimePart(appointment.time, 0);

        let endTimeRaw =
          appointment.endTime || extractTimePart(appointment.time, 1);

        const formattedDate = formatDateLong(appointmentDateValue);
        const formattedStart = formatTimeDisplay(startTimeRaw);
        const endDisplayCandidate = formatTimeDisplay(endTimeRaw);
        const formattedEnd =
          endDisplayCandidate === "N/A" ? formattedStart : endDisplayCandidate;

        const appointmentWindow = `${formattedDate} (${formattedStart} - ${formattedEnd})`;
        const dentistMessage = `Your upcoming patient appointment on ${appointmentWindow} has been removed because the patient is no longer in the clinic’s records.`;

        try {
          await addNotification(appointment.dentistId, dentistMessage);
        } catch (notificationError) {
          console.error(
            `Failed to send notification to dentist ${appointment.dentistId}:`,
            notificationError
          );
        }
      }

      setPatients((prev) => prev.filter((p) => p.uid !== patientToRemove.uid));
      setFilteredPatients((prev) =>
        prev.filter((p) => p.uid !== patientToRemove.uid)
      );

      if (
        selectedPatientInfo &&
        selectedPatientInfo.uid === patientToRemove.uid
      ) {
        setSelectedPatientInfo(null);
        setSelectedPatientName({ firstName: "", middleName: "", lastName: "" });
        setPatientRecords([]);
      }

      setShowRemoveModal(false);
      setPatientToRemove(null);

      setSnackbarSeverity("success");
      setSnackbarMessage(
        `A patient ${patientName || "N/A"} has been successfully removed.`
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error removing patient:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to remove patient. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setIsRemovingPatient(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Function to cancel patient removal
  const cancelRemovePatient = async () => {
    setIsCancelling(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setShowRemoveModal(false);
    setPatientToRemove(null);
    setIsCancelling(false);
  };

  // Function to close snackbar
  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Function to handle update password button click
  const handleUpdatePasswordClick = () => {
    if (!selectedPatientInfo) return;
    setShowUpdatePasswordModal(true);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordErrors({ newPassword: "", confirmPassword: "" });
  };

  // Function to close update password modal
  const handleCloseUpdatePasswordModal = () => {
    setShowUpdatePasswordModal(false);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordErrors({ newPassword: "", confirmPassword: "" });
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // Password pattern from sign-up form
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).+$/;

  // Function to validate password (matching sign-up form validation)
  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters long";
    if (!passwordPattern.test(password)) {
      return "Password must contain an uppercase letter, a lowercase letter, a number, and a special character";
    }
    return "";
  };

  // Function to validate confirm password
  const validateConfirmPassword = (confirmPasswordValue, newPasswordValue) => {
    if (!confirmPasswordValue) return "Confirm password is required";
    if (confirmPasswordValue !== newPasswordValue) return "Passwords do not match";
    return "";
  };

  // Function to handle password update
  const handleUpdatePassword = async () => {
    if (!selectedPatientInfo || !selectedPatientInfo.uid) {
      setSnackbarSeverity("error");
      setSnackbarMessage("No patient selected.");
      setSnackbarOpen(true);
      return;
    }

    // Validate passwords
    const newPasswordError = validatePassword(newPassword);
    const confirmPasswordError = validateConfirmPassword(confirmPassword, newPassword);

    setPasswordErrors({
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError,
    });

    if (newPasswordError || confirmPasswordError) {
      return;
    }

    setIsUpdatingPassword(true);

    try {
      // Get patient's authEmail from Firestore
      const patientDoc = await getDoc(doc(db, "users", selectedPatientInfo.uid));
      if (!patientDoc.exists()) {
        throw new Error("Patient not found");
      }

      const patientData = patientDoc.data();
      const authEmail = patientData.authEmail || patientData.email;

      if (!authEmail) {
        throw new Error("Patient email not found");
      }

      // Call Cloud Function to update password
      const functions = getFunctions();
      const updateUserPassword = httpsCallable(functions, 'updateUserPassword');
      
      await updateUserPassword({ 
        uid: selectedPatientInfo.uid, 
        newPassword: newPassword 
      });

      setSnackbarSeverity("success");
      setSnackbarMessage("Patient password updated successfully.");
      setSnackbarOpen(true);
      handleCloseUpdatePasswordModal();
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error.message || "Failed to update password. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const tooltipContent = (
    <Box
      sx={{
        display: {
          xs: "none",
          sm: "none",
          md: "flex",
          lg: "none",
          xl: "none",
        },
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography variant="body3">
        Search
      </Typography>
      <IconButton
        size="small"
        onClick={handleTooltipClose}
        sx={{
          color: "#FEFEFE",
          padding: "2px",
          "&:hover": { opacity: 0.8 },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const boxStylesContent = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#FDF9F3",
    // width: "100%"
  };


  const labelStyles = {
    color: "rgba(44, 37, 30, .50)",
    fontWeight: 500,
    padding: "5px 0px",
    fontSize: "14px",
  };

  const detailStyles = {
    color: "2C251E",
    fontWeight: 500,
    fontSize: "14px",
    padding: "5px 0px",
  };

  const patientNamePendingRemoval = formatPatientFullName(patientToRemove);

  return (
    <div>
      <SplashScreen isOpen={showSplash} />
      {/* All Content */}
      <Box sx={boxStylesContent}>
        {/* Sidebar */}
        <Box
          display={{
            xs: "none",
            sm: "none",
            md: "block",
            lg: "block",
            xl: "block",
          }}>
        <Sidebar
          fName={userDetails?.firstName}
          lName={userDetails?.lastName}
          email={userDetails?.email}
        />
        </Box>

        {/*Grids */}
        <Grid
          container
          // rowSpacing={2}
          // columnSpacing={2}
          sx={{
            width: "100%",
            height: "100vh",
            // backgroundColor: "blue",
            "& .MuiGrid-item": {
              display: "flex",
              justifyContent: "center",
              // backgroundColor: "blue",
            },
          }}
        >
          {/* Main Content */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
          >
            {/* Main Content Area: header and content in a column */}
            <div>
              <Header
                count={2}
                page="Patient Records"
                handleSearch={handleSearch}
                value={searchTerm}
                setSearchTerm={setSearchTerm}
                setHandleSearch={handleSearch}
              />

              {/* Main Content Container with padding */}
              <Box 
                sx={{ 
                  padding: {
                    xs: "0",
                    sm: "20px",
                    md: "20px",
                    lg: "20px",
                    xl: "20px",
                  },
                  flex: 1, 
                }}>
                {/* Main Content Row (table and patient info) */}
                <div
                  style={{
                    display: "flex",
                    gap: 40,
                    justifyContent: "center",
                    alignItems: "flex-start",
                    padding: "0",
                  }}
                >
                  
                  {/* Table Column: let it grow so table gets more width */}
                  <Box 
                    sx={{ 
                      display: {
                        xs: selectedPatient && selectedPatientInfo ? "none" : "block",
                        sm: selectedPatient && selectedPatientInfo ? "none" : "block",
                        md: selectedPatient && selectedPatientInfo ? "none" : "block",
                        lg: selectedPatient && selectedPatientInfo ? "none" : "block",
                        xl: "flex",
                      },
                      flexDirection: "column", 
                      alignItems: "stretch", 
                      flex: 1,
                      // width: "100%",
                    }}>
                    {/* <Stack
                      display={{ 
                        xs: "flex",
                        sm: "none",
                        md: "none",
                        lg: "none",
                        xl: "none",
                      }}
                      direction={{
                        xs: "column",
                        sm: "row",
                        md: "row",
                        lg: "row",
                        xl: "row",
                      }}
                      padding={{
                        xs: "20px 20px 20px 20px",
                        sm: "0 20px 20px 20px",
                        md: "0 0 20px 0",
                        lg: "0 0 20px 0",
                        xl: "0 0 20px 0",
                      }}
                      alignItems={{
                        xs: "none",
                        sm: "center",
                        md: "center",
                        lg: "center",
                        xl: "center",
                      }}
                      justifyContent="space-between"
                      spacing={{
                        xs: 2,
                        sm: 0,
                        md: 0,
                        lg: 0,
                        xl: 0,
                      }}
                      >
                      <Search
                          handleSearch={handleSearch}
                          page="manageServices"
                          value={searchTerm}
                      />

                    </Stack> */}
                    {!selectedPatient && !selectedPatient && (
                        <Box
                          sx={{
                            display: {
                              xs: "block",
                              sm: "none",
                              md: "none",
                              lg: "none",
                              xl: "none",
                            },
                            maxHeight: "85vh",
                            minHeight: "600px",
                            overflow: "auto"
                          }}>
                      {filteredPatients.length === 0 ? (
                        <Stack
                                direction="column"
                                spacing={3}
                                sx={{
                                  padding: "50px",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  justifyItems: "center",
                                  alignContent: "center",
                                  alignItems: "center"
                                }}>

                                  <Box
                                    sx={{
                                      backgroundColor: "rgba(46, 45, 45, 0.1)",
                                      borderRadius: "150px",
                                      padding: "20px"
                                    }}>
                                      
                                  <Icon
                                    src={require("../../../assets/icons/document.png")}
                                    size="user-image"/>
                                    </Box>

                                  <Stack
                                    direction="column"
                                    spacing={1}>

                                    <Text
                                      text="Oops looks empty here!"
                                      variant="historyService"/>

                                    <Text
                                      text="No patients found."
                                      variant="body1"/>

                                  </Stack>
                                  </Stack>
                      ) : (
                        currentPatients.map((patient) => {
                            const isSelected = selectedPatient === patient.uid;
                            return (
                              <React.Fragment key={patient.uid}>
                                {/* Add a spacer row before the first data row */}
                                <Box
                                  key={patient.uid}
                                  onClick={() =>
                                    handlePatientClick(patient.uid)
                                  }
                                  sx={{
                                    padding: "10px",
                                    cursor: "pointer",
                                    backgroundColor: isSelected
                                      ? "#2E2D2D"
                                      : "#FEFEFE",
                                    // border: "1px solid black",
                                    margin: "20px",
                                    borderRadius: "10px",
                                    border: "1px solid rgba(89, 84, 74, .20)", 
                                    boxShadow: "0px 2px 4px .1px rgba(89, 84, 74, .5)",
                                  }}
                                  >

                                    <Stack direction="row">

                                      <Avatar
                                        alt="Profile"
                                        sx={{
                                          width: "50px",
                                          height: "50px",
                                          border: "3px solid #59544A",
                                          backgroundColor: "#2C251E"
                                        }}
                                        src={
                                          patient?.profilePictureUrl
                                            ? patient.profilePictureUrl
                                            : null
                                        }
                                      >
                                        {!patient?.profilePictureUrl && (
                                          <Icon
                                            src={user}
                                            size="small"/>
                                        )}
                                      </Avatar>

                                      <Stack
                                        direction="column"
                                        spacing={1}
                                      sx={{
                                        justifyContent: "center",
                                      }}>
                                        <Text
                                          text={patient.firstName  + " " + patient.middleName + " " + patient.lastName}
                                          variant="patientNameRecords"/>
                                          <Stack
                                           direction="row"
                                           spacing={1}>
                                        <Text
                                          text={patient.contactNumber}
                                          variant="indexHeroH3"/>

                                        <Text
                                          text={patient.email}
                                          variant="indexHeroH3"/>
                                          </Stack>
                                      </Stack>
                                    </Stack>
                                  
                                  </Box>
                                  </React.Fragment>
                                )})
                       )}
                       </Box>
                    )}

                    <TableContainer
                      component={Paper}
                      sx={{
                        display: {
                          xs: "none",
                          sm: "block",
                          md: "block",
                          lg: "block",
                          xl: "block",
                        },
                        width: "100%", // take full width of the left column
                        boxShadow: {
                          xs: null,
                          sm: "0px 2px 8px .5px #59544A",
                          md: "0px 2px 8px .5px #59544A",
                          lg: "0px 2px 8px .5px #59544A",
                          xl: "0px 2px 8px .5px #59544A",
                        },
                        maxHeight: "650px",
                        minHeight: "650px",
                        margin: {
                          xs: null,
                          sm: null,
                          md: null,
                          lg: null,
                          xl: "15px 8px 15px 15px",
                        },
                        borderRadius: {
                          xs: "0",
                          sm: "20px",
                          md: "20px",
                          lg: "20px",
                          xl: "20px",
                        },
                      }}
                    >
                    <Table
                      aria-label="simple table"
                      stickyHeader
                      sx={{
                        padding: "0 20px 20px 20px",
                        // margin: "20px 0"
                      }}
                    >
                      {/* Table Head */}
                      <TableHead>
                        <TableRow
                          sx={
                            {
                              // backgroundColor: "mediumaquamarine"
                              // padding: "20px"
                            }
                          }
                        >
                          <TableCell
                            sx={{
                              // backgroundColor: "mediumaquamarine",
                              borderBottom: "2px solid rgba(89, 84, 74, 1)",
                              color: "rgba(44, 37, 30, .8)",
                              padding: "30px 10px 20px 10px",
                              fontWeight: "600",
                              // margin: "20px"
                            }}
                          >
                            Last Name
                          </TableCell>
                          <TableCell
                            sx={{
                              // backgroundColor: "mediumaquamarine",
                              borderBottom: "2px solid rgba(89, 84, 74, 1)",
                              color: "rgba(44, 37, 30, .8)",
                              padding: "30px 10px 20px 10px",
                              fontWeight: "600",
                            }}
                          >
                            First Name
                          </TableCell>
                          <TableCell
                            sx={{
                              // backgroundColor: "mediumaquamarine",
                              borderBottom: "2px solid rgba(89, 84, 74, 1)",
                              color: "rgba(44, 37, 30, .8)",
                              padding: "30px 10px 20px 10px",
                              fontWeight: "600",
                            }}
                          >
                            Middle Name
                          </TableCell>
                          <TableCell
                            sx={{
                              // backgroundColor: "mediumaquamarine",
                              borderBottom: "2px solid rgba(89, 84, 74, 1)",
                              color: "rgba(44, 37, 30, .8)",
                              padding: "30px 10px 20px 10px",
                              fontWeight: "600",
                            }}
                          >
                            Email Address
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      {/* Body */}
                      <TableBody>
                        {filteredPatients.length === 0 ? (
                          <TableRow>
                            <TableCell
                              sx={{
                                borderBottom: "none",
                                padding: "20px 10px 10px 10px",
                              }}
                            >
                              Not found.
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: "none",
                                padding: "20px 10px 10px 10px",
                              }}
                            >
                              Not found.
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: "none",
                                padding: "20px 10px 10px 10px",
                              }}
                            >
                              Not found.
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: "none",
                                padding: "20px 10px 10px 10px",
                              }}
                            >
                              Not found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          currentPatients.map((patient) => {
                            const isSelected = selectedPatient === patient.uid;
                            return (
                              <React.Fragment key={patient.uid}>
                                {/* Add a spacer row before the first data row */}
                                <TableRow
                                  key={patient.uid}
                                  onClick={() =>
                                    handlePatientClick(patient.uid)
                                  }
                                  sx={{
                                    cursor: "pointer",
                                    backgroundColor: isSelected
                                      ? "#2E2D2D"
                                      : "#FEFEFE",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      color: isSelected ? "#FDF9F3" : "#2C251E",
                                      borderBottom:
                                        "1px solid rgba(89, 84, 74, .3)",
                                      borderTopLeftRadius: isSelected
                                        ? "10px"
                                        : "none",
                                      borderBottomLeftRadius: isSelected
                                        ? "10px"
                                        : "none",
                                      padding: isSelected
                                        ? "20px 0px 20px 20px"
                                        : "20px 10px 10px 10px",
                                      // 30px 20px 10px 20px
                                      transition: "padding 0.5s ease",
                                    }}
                                  >
                                    {patient.lastName}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: isSelected ? "#FDF9F3" : "#2C251E",
                                      borderBottom:
                                        "1px solid rgba(89, 84, 74, .3)",
                                      padding: isSelected
                                        ? "20px 0px 20px 20px"
                                        : "20px 10px 10px 10px",
                                      transition: "padding 0.5s ease",
                                    }}
                                  >
                                    {patient.firstName}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: isSelected ? "#FDF9F3" : "#2C251E",
                                      borderBottom:
                                        "1px solid rgba(89, 84, 74, .3)",
                                      padding: isSelected
                                        ? "20px 0px 20px 20px"
                                        : "20px 10px 10px 10px",
                                      transition: "padding 0.5s ease",
                                    }}
                                  >
                                    {patient.middleName &&
                                    patient.middleName !== "--"
                                      ? patient.middleName
                                      : "--"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: isSelected ? "#FDF9F3" : "#2C251E",
                                      borderBottom:
                                        "1px solid rgba(89, 84, 74, .3)",
                                      borderTopRightRadius: isSelected
                                        ? "10px"
                                        : "none",
                                      borderBottomRightRadius: isSelected
                                        ? "10px"
                                        : "none",
                                      padding: isSelected
                                        ? "20px 0px 20px 20px"
                                        : "20px 10px 10px 10px",
                                      transition: "padding 0.5s ease",
                                    }}
                                  >
                                    {formatContactInfo(patient)}
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!selectedPatient && !selectedPatient && (
                   <Box
                     sx={{
                       width: "100%",
                       display: "flex",
                       justifyContent: "center",
                       mt: 1,
                      mb: 2,
                     }}
                   >
                     <Pagination
                      count={totalPatientPages}
                      page={patientsPage}
                      onChange={(e, page) => setPatientsPage(page)}
                      shape="rounded"
                      variant="outlined"
                      color="primary"
                      siblingCount={1}      
                      boundaryCount={1}     
                      // showFirstButton
                      // showLastButton
                     />
                   </Box>
                   )}

                  {selectedPatient && selectedPatient && (
                   <Box
                     sx={{
                       width: "100%",
                       display: "flex",
                       justifyContent: "center",
                       mt: 1,
                      mb: 2,
                     }}
                   >
                     <Pagination
                      count={totalPatientPages}
                      page={patientsPage}
                      onChange={(e, page) => setPatientsPage(page)}
                      shape="rounded"
                      variant="outlined"
                      color="primary"
                      siblingCount={1}      
                      boundaryCount={1}     
                      // showFirstButton
                      // showLastButton
                     />
                   </Box>
                   )}
                 </Box>

                  {/* Patient Details Card */}
                  <Box
                    sx={{
                      display: {
                        xs: selectedPatient && selectedPatientInfo ? "contents" : "none",
                        sm: selectedPatient && selectedPatientInfo ? "contents" : "none",
                        md: selectedPatient && selectedPatientInfo ? "contents" : "none",
                        lg: selectedPatient && selectedPatientInfo ? "contents" : "none",
                        xl: "flex",
                      },
                      flex: "0 0 auto",
                      // minWidth: 300,
                      maxWidth: 360, // reduced width
                      minHeight: "600px",
                      maxHeight: "600px",
                      padding: "26px",
                      borderRadius: "20px",
                      margin: "15px 15px 15px 5px",
                      background: "#FDF9F3",
                      justifyContent: "flex-start",
                      boxShadow: "0px 2px 8px .5px #59544A",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        // width: 420,
                        background: "#FDF9F3",
                        borderRadius: 22,
                        // boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                        // padding: 32,
                        marginTop: 0,
                        // borderBottom: "1px solid black"
                        // border: '1px solid #ede7df',
                        // minHeight: 420
                        // maxHeight: "640px",
                        // // minHeight: "640px",
                      }}
                    >
                      {selectedPatient && selectedPatientInfo ? (
                        <>
                          <Box
                            sx={{
                              fontWeight: 700,
                              // fontSize: 20,
                              margin: "20px",
                              color: "#3d342b",
                              backgroundColor: "#FDF9F3",
                              // transition: '0.5s ease',
                              // borderBottom: "1px solid rgba(89, 84, 74, .2)"
                            }}
                          >
                          <Box
                            onClick={() => setSelectedPatient(null)}
                            sx={{
                              width:"30px",
                              height: "10px",
                              display: {
                                xs: "flex",
                                sm: "flex",
                                md: "flex",
                                lg: "flex",
                                xl: "none",
                              }
                            }}>
                              <Icon
                                src={require("../../../assets/back.png")}
                                size="small"
                              />
                            </Box>
                            {/* Title & Picture */}
                            <Stack
                              direction="row"
                              sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingBottom: "20px",
                                fontSize: 20,
                                borderBottom: "2px solid rgba(89, 84, 74, .2)",
                              }}
                            >
                              Patient Information
                              {/* Profile Image */}
                              <Box
                                sx={{
                                  borderRadius: "50%",
                                  border: "3px solid #59544A",
                                  overflow: "hidden",
                                  position: "relative",
                                  height: "100px",
                                  width: "100px",
                                  background: "#59544A",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {selectedPatientInfo?.profilePictureUrl ? (
                                  <Box>
                                    <img
                                      src={
                                        selectedPatientInfo?.profilePictureUrl
                                          ? selectedPatientInfo.profilePictureUrl
                                          : user
                                      }
                                      alt="Profile"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: "50%",
                                        backgroundColor: "black",
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      backgroundColor: "#2C251E",
                                      // borderRadius: "10px",
                                      // width: "50px",
                                      // height: "50px",
                                      // padding: "30px",
                                      width: "100px",
                                      height: "100px",
                                      alignContent: "center",
                                      alignItems: "center",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Icon src={user} size="large" />
                                  </Box>
                                )}
                              </Box>
                            </Stack>

                            {/* Details */}
                            <Stack
                              direction="column"
                              sx={{
                                borderBottom: "2px solid rgba(89, 84, 74, .2)",
                                padding: "10px 0px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 17,
                                  color: "#3d342b",
                                  padding: "10px 0px",
                                }}
                              >
                                Details
                              </div>

                              {/*Grids */}
                              <Grid
                                container
                                // rowSpacing={2}
                                // columnSpacing={2}
                                sx={{
                                  width: "100%",
                                  // height: "100vh",
                                  // backgroundColor: "blue",
                                  "& .MuiGrid-item": {
                                    display: "flex",
                                    justifyContent: "center",
                                    // backgroundColor: "blue",
                                  },
                                }}
                              >
                                {/* Name */}
                                <Grid
                                  size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                                  item
                                >
                                  <div style={labelStyles}>Name</div>
                                </Grid>
                                <Grid
                                  size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}
                                  item
                                >
                                  <div style={detailStyles}>
                                    {selectedPatientInfo.firstName
                                      ? selectedPatientInfo.firstName
                                      : "N/A"}{" "}
                                    {selectedPatientInfo.middleName &&
                                    selectedPatientInfo.middleName !== "--"
                                      ? selectedPatientInfo.middleName + " "
                                      : selectedPatientInfo.middleName ===
                                          "--" ||
                                        !selectedPatientInfo.middleName
                                      ? ""
                                      : selectedPatientInfo.middleName + " "}
                                    {selectedPatientInfo.lastName
                                      ? selectedPatientInfo.lastName
                                      : "N/A"}
                                  </div>
                                </Grid>

                                {/* Birthdate */}
                                <Grid
                                  size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                                  item
                                >
                                  <div style={labelStyles}>Birthdate</div>
                                </Grid>
                                <Grid
                                  size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}
                                  item
                                >
                                  <div style={detailStyles}>
                                    {selectedPatientInfo &&
                                    (selectedPatientInfo.birthDate ||
                                      selectedPatientInfo.birthday ||
                                      selectedPatientInfo.birthdate)
                                      ? formatDateMMDDYYYY(
                                          selectedPatientInfo.birthDate ||
                                            selectedPatientInfo.birthday ||
                                            selectedPatientInfo.birthdate
                                        )
                                      : "N/A"}
                                  </div>
                                </Grid>

                                {/* Age */}
                                <Grid
                                  size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                                  item
                                >
                                  <div style={labelStyles}>Age</div>
                                </Grid>
                                <Grid
                                  size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}
                                  item
                                >
                                  <div style={detailStyles}>
                                    {selectedPatientInfo.age
                                      ? selectedPatientInfo.age
                                      : "N/A"}
                                  </div>
                                </Grid>

                                {/* Address */}
                                <Grid
                                  size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                                  item
                                >
                                  <div style={labelStyles}>Address</div>
                                </Grid>
                                <Grid
                                  size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}
                                  item
                                >
                                  <div style={detailStyles}>
                                    {selectedPatientInfo.address
                                      ? selectedPatientInfo.address
                                      : "N/A"}
                                  </div>
                                </Grid>

                                {/* Email */}
                                <Grid
                                  size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                                  item
                                >
                                  <div style={labelStyles}>Email</div>
                                </Grid>
                                <Grid
                                  size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}
                                  item
                                >
                                  <div style={detailStyles}>
                                    {selectedPatientInfo.email
                                      ? selectedPatientInfo.email
                                      : "N/A"}
                                  </div>
                                </Grid>

                                {/* Civil Status */}
                                <Grid
                                  size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                                  item
                                >
                                  <div style={labelStyles}>Civil Status</div>
                                </Grid>
                                <Grid
                                  size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}
                                  item
                                >
                                  <div style={detailStyles}>
                                    {selectedPatientInfo.civilStatus
                                      ? selectedPatientInfo.civilStatus
                                      : "N/A"}
                                  </div>
                                </Grid>

                                {/* Mobile Number */}
                                <Grid
                                  size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                                  item
                                >
                                  <div style={labelStyles}>Mobile Number</div>
                                </Grid>
                                <Grid
                                  size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}
                                  item
                                >
                                  <div style={detailStyles}>
                                    {selectedPatientInfo.contactNumber
                                      ? selectedPatientInfo.contactNumber.toString()
                                          .length === 11 &&
                                        selectedPatientInfo.contactNumber
                                          .toString()
                                          .startsWith("09")
                                        ? `${selectedPatientInfo.contactNumber
                                            .toString()
                                            .slice(
                                              0,
                                              4
                                            )}-${selectedPatientInfo.contactNumber
                                            .toString()
                                            .slice(
                                              4,
                                              7
                                            )}-${selectedPatientInfo.contactNumber
                                            .toString()
                                            .slice(7)}`
                                        : selectedPatientInfo.contactNumber.toString()
                                      : "N/A"}
                                  </div>
                                </Grid>
                              </Grid>
                            </Stack>

                            {/* Action Buttons */}
                            <Stack
                              direction="column"
                              spacing={1}
                              sx={{
                                // borderBottom: "2px solid rgba(89, 84, 74, .2)",
                                padding: "10px 0px",
                              }}
                            >
                              <Stack
                                direction="row"
                                sx={{
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 17,
                                    color: "#3d342b",
                                  }}
                                >
                                  Treatment History
                                </div>

                                <button
                                  onClick={() => setShowTreatmentHistory(true)}
                                  style={{
                                    background: "#1976d2",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "10px 15px",
                                    fontWeight: 600,
                                    fontSize: 15,
                                    cursor: "pointer",
                                  }}
                                >
                                  View
                                </button>
                              </Stack>

                              <Stack
                                direction="row"
                                sx={{
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 17,
                                    color: "#3d342b",
                                  }}
                                >
                                  Medical History
                                </div>
                                <button
                                  onClick={() => setActiveTab("medical")}
                                  style={{
                                    background: "#1976d2",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "10px 15px",
                                    fontWeight: 600,
                                    fontSize: 15,
                                    cursor: "pointer",
                                  }}
                                >
                                  View
                                </button>
                              </Stack>

                              <Stack
                                direction="row"
                                sx={{
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 17,
                                    color: "#3d342b",
                                  }}
                                >
                                  Dental Chart
                                </div>
                                <button
                                  onClick={() => setShowDentalChart(true)}
                                  style={{
                                    background: "#1976d2",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "10px 15px",
                                    fontWeight: 600,
                                    fontSize: 15,
                                    cursor: "pointer",
                                  }}
                                >
                                  View
                                </button>
                              </Stack>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Typography
                                  onClick={handleUpdatePasswordClick}
                                  gutterBottom
                                  variant="h3"
                                  component="div"
                                  sx={{
                                    cursor: "pointer",
                                    fontWeight: "300",
                                    fontSize: "14px",
                                    paddingTop: "10px",
                                    margin: "4px 0px 0px 0px",
                                    color: "rgb(25, 118, 210)",
                                    ":hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  Update Password
                                </Typography>
                                {/* {(userDetails?.role || userDetails?.userType) !== "AssociateDentist" && (
                                  <Typography
                                    onClick={() =>
                                      handleRemovePatient(selectedPatientInfo)
                                    }
                                    gutterBottom
                                    variant="h3"
                                    component="div"
                                    sx={{
                                      cursor: "pointer",
                                      fontWeight: "300",
                                      fontSize: "14px",
                                      paddingTop: "10px",
                                      margin: "4px 0px 0px 0px",
                                      color: "#9F4223",
                                      ":hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    Remove Patient
                                  </Typography>
                                )} */}
                              </Box>
                            </Stack>
                          </Box>

                          {/* Medical History Modal */}
                          <Modal
                            isOpen={activeTab === "medical"}
                            onRequestClose={() => setActiveTab("personal")}
                            style={{
                              overlay: {
                                zIndex: 2000,
                                backgroundColor: "rgba(0,0,0,0.7)",
                              },
                              content: {
                                width: "80%",
                                margin: "0 auto",
                                position: "absolute",
                                padding: "0",
                                maxHeight: "calc(100vh - 100px)",
                                overflow: "hidden",
                                top: "20px",
                                borderRadius: "16px",
                                backgroundColor: "rgb(241, 241, 241)",
                                display: "flex",
                                flexDirection: "column",
                              },
                            }}
                          >
                            {/* Fixed Header */}
                            <div
                              style={{
                                position: "initial",
                                top: 0,
                                backgroundColor: "rgb(241, 241, 241)",
                                margin: "16px",
                                zIndex: 10,
                                borderBottom: "1px solid #e0e0e0",
                              }}
                            >
                              {/* Close button - top right, circular */}
                              <button
                                onClick={() => setActiveTab("personal")}
                                style={{
                                  position: "absolute",
                                  top: 16,
                                  right: 16,
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  border: "1px solid rgba(44,37,30,0.06)",
                                  background: "#fff",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  color: "#2E2D2D",
                                  transition: "all 0.2s ease",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "#C5A572";
                                  e.target.style.color = "#FEFEFE";
                                  e.target.style.borderColor = "#C5A572";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "#fff";
                                  e.target.style.borderColor = "rgba(44,37,30,0.06)";
                                  e.target.style.color = "#2E2D2D";
                                }}
                              >
                                ✕
                              </button>

                              {/* Centered title */}
                              <h3
                                style={{
                                  textAlign: "center",
                                  width:{
                                    xs: "",
                                    sm: "",
                                    md: "",
                                    lg: "",
                                    xl: "250px"
                                  },
                                  marginBottom: "10px",
                                  marginTop: "40px",
                                  fontSize: "24px",
                                  fontWeight: "bold",
                                  color: "#3d342b",
                                }}
                              >
                                Medical History for{" "}
                                {selectedPatientName.firstName}{" "}
                                {selectedPatientName.middleName}{" "}
                                {selectedPatientName.lastName}
                              </h3>
                            </div>

                            {/* Scrollable Content */}
                            <div
                              style={{
                                // flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                // padding: "5px",
                                // paddingRight: "8px",
                                // paddingBottom: "16px",
                                minHeight: 0,
                              }}
                            >
                              <MedicalHistory
                                ref={medicalHistoryRef}
                                patientId={selectedPatientInfo?.uid}
                              />
                            </div>

                            {/* Fixed Footer with Save Button */}
                            <div
                              style={{
                                flexShrink: 0,
                                backgroundColor: "rgb(241, 241, 241)",
                                padding: "16px",
                                textAlign: "center",
                                borderTop: "1px solid #e0e0e0",
                              }}
                            >
                              <button
                                onClick={async () => {
                                  if (medicalHistoryRef.current) {
                                    await medicalHistoryRef.current.save();
                                    // Show snackbar
                                    setSnackbarMessage("Medical History Saved!");
                                    setSnackbarSeverity("success");
                                    setSnackbarOpen(true);
                                    // Close the modal after saving
                                    setActiveTab("personal");
                                  }
                                }}
                                style={{
                                  background: "rgb(197, 165, 114)",
                                  color: "#fff",
                                  border: "none",
                                  padding: "10px 28px",
                                  borderRadius: "6px",
                                  fontSize: "1.08rem",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  transition: "background 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "rgb(185, 153, 102)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "rgb(197, 165, 114)";
                                }}
                              >
                                Save Medical History
                              </button>
                            </div>
                          </Modal>

                          {/* Treatment History Modal */}
                          <Modal
                            isOpen={showTreatmentHistory}
                            onRequestClose={handleTreatmentHistoryClose}
                        style={{
                        overlay: {
                          zIndex: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          // padding: "16px",
                        },
                        content: {
                          // inset: "unset",
                          border: "none",
                          borderRadius: "16px",
                          // background: "#aa6666ff",
                          // padding: "24px",
                          width: "80%",
                          maxHeight: "85vh",
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          // justifyContent: "center",
                          // alignItems: "center",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                          // Responsive adjustments
                          transition: "all 0.3s ease",
                          top: "50%",
                          left: "50%",
                          right: "auto",
                          bottom: "auto",
                          // margin: "20px",
                          // // marginRight: "-50%",
                          transform: "translate(-50%, -50%)",
                          // padding: "35px",
                          maxWidth: "80%",
                          minWidth: "180px"
                        },
                      }}
                          >
                            <PersonnelTreatmentHistoryModal
                              appointments={patientRecords}
                              onClose={handleTreatmentHistoryClose}
                              handleViewInsuranceDetails={
                                handleViewInsuranceDetails
                              }
                              patientName={`${selectedPatientName.firstName} ${selectedPatientName.middleName} ${selectedPatientName.lastName}`}
                              patientUid={selectedPatientInfo?.uid}
                              onRemarksSaved={handleRemarksSaved}
                            userDetails={userDetails}
                            />

                          </Modal>

                          {/* Dental Chart Modal */}
                          <Modal
                            isOpen={showDentalChart}
                            onRequestClose={handleDentalChartClose}
                      style={{
                        overlay: {
                          zIndex: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "16px",
                        },
                        content: {
                          // inset: "unset",
                          border: "none",
                          borderRadius: "16px",
                          background: "#FEFEFE",
                          // padding: "24px",
                          width: "75%",
                          maxHeight: "85vh",
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                          // Responsive adjustments
                          transition: "all 0.3s ease",
                          top: "50%",
                          left: "50%",
                          right: "auto",
                          bottom: "auto",
                          overflowX: "hidden",
                          // margin: "20px",
                          marginRight: "-50%",
                          transform: "translate(-50%, -50%)",
                          maxWidth: "800px",
                          minWidth: "300px"
                        },
                      }}
                          >
                            {/* Close button */}
                            <button
                              onClick={handleDentalChartClose}
                              style={{
                                position: "absolute",
                                top: 18,
                                right: 18,
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                border: "1px solid rgba(44,37,30,0.06)",
                                background: "#FDF9F3",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "18px",
                                fontWeight: "bold",
                                color: "#2E2D2D",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#C5A572";
                                e.target.style.color = "#FEFEFE";
                                e.target.style.borderColor = "#C5A572";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "#fff";
                                e.target.style.color = "#2E2D2D";
                                e.target.style.borderColor = "rgba(44,37,30,0.06)";
                              }}
                              aria-label="Close"
                            >
                              ✕
                            </button>
                            <h3
                              style={{
                                textAlign: "center",
                                marginBottom: "18px",
                                marginTop: "10px",
                                fontSize: "26px",
                                fontWeight: 700,
                                color: "#3d342b",
                                letterSpacing: 0.5,
                              }}
                            >
                              Dental Chart for {selectedPatientName.firstName}{" "}
                              {selectedPatientName.middleName}{" "}
                              {selectedPatientName.lastName}
                            </h3>
                            <DentalChart uid={selectedPatientInfo?.uid} />
                          </Modal>
                        </>
                      ) : (
                        <div
                          style={{
                            color: "#2C251E",
                            fontSize: 18,
                            margin: "30px",
                            fontWeight: "600",
                          }}
                        >
                          Select a patient.
                        </div>
                      )}
                    </div>
                  </Box>
                </div>
              </Box>
            </div>
          </Grid>
        </Grid>
      </Box>

      <SplashScreen
        isOpen={showCustomAlert}
        image={alertImage}
        button={alertButton}
        buttonType={alertButtonType}
        destination={alertLink}
        onClose={() => setShowCustomAlert(false)}
      />

      {/* Render ViewInsurance modal at the top level, not nested */}
      <ViewInsurance
        isOpen={showInsuranceForm}
        onClose={handleInsuranceClose}
        insuranceDetails={insuranceDetails}
      />

      {/* Remove Patient Confirmation Dialog */}
      <Dialog
        open={showRemoveModal}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          // Allow closing on backdrop click; keep Esc disabled by prop
          cancelRemovePatient();
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: "#FFFFFF",
            paddingBottom: 2,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#C94D34",
            fontSize: "22px",
            padding: "28px 32px 12px 32px",
            position: "relative",
          }}
        >
          Remove Patient
          <IconButton
            onClick={cancelRemovePatient}
            disabled={isCancelling || isRemovingPatient}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: isCancelling ? "#B0A99F" : "#6D6257",
              "&:hover": {
                backgroundColor: isCancelling ? "transparent" : "rgba(109, 98, 87, 0.08)",
              },
            }}
          >
            {isCancelling ? (
              <CircularProgress size={18} sx={{ color: "#B0A99F" }} />
            ) : (
              <Close fontSize="small" />
            )}
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 32px 8px 32px" }}>
          <Stack spacing={2} alignItems="center">
            <Typography
              sx={{
                color: "#2C251E",
                fontSize: "16px",
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              {`Are you sure you want to remove ${
                patientNamePendingRemoval || "this patient"
              }?`}
            </Typography>
            <Typography
              sx={{
                color: "rgba(44, 37, 30, 0.6)",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              This action cannot be undone. Patient account will be deleted.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "12px 32px 28px 32px",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button
            onClick={cancelRemovePatient}
            disabled={isCancelling || isRemovingPatient}
            variant="outlined"
            sx={{
              color: isCancelling ? "#B0A99F" : "#6D6257",
              borderColor: isCancelling ? "#D3CBC1" : "#6D6257",
              padding: "10px 22px",
              borderRadius: 20,
              fontSize: "15px",
              fontWeight: 600,
              minWidth: "120px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: isCancelling ? "transparent" : "rgba(109, 98, 87, 0.08)",
                borderColor: isCancelling ? "#D3CBC1" : "#6D6257",
              },
              "&:disabled": {
                borderColor: "#D3CBC1",
                color: "#B0A99F",
              },
            }}
          >
            {isCancelling ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#B0A99F" }} />
                <span>Cancelling...</span>
              </Box>
            ) : (
              "No, Cancel"
            )}
          </Button>
          <Button
            onClick={confirmRemovePatient}
            disabled={isRemovingPatient || isCancelling}
            variant="contained"
            sx={{
              backgroundColor: isRemovingPatient ? "#D3CBC1" : "#C94D34",
              color: "#fff",
              padding: "10px 22px",
              borderRadius: 20,
              fontSize: "15px",
              fontWeight: 600,
              minWidth: "120px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: isRemovingPatient ? "#D3CBC1" : "#A93F28",
                boxShadow: "none",
              },
              "&:disabled": {
                backgroundColor: "#D3CBC1",
                color: "#F5F1EC",
              },
            }}
          >
            {isRemovingPatient ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                <span>Removing...</span>
              </Box>
            ) : (
              "Yes, Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Password Modal */}
      <Dialog
        open={showUpdatePasswordModal}
        onClose={handleCloseUpdatePasswordModal}
        maxWidth="sm"
        fullWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            overflow: "hidden",
            bgcolor: "#FDF9F3",
            boxShadow: "0 18px 60px rgba(44,37,30,0.14)",
            width: "500px",
            maxWidth: "90vw",
          },
          "& .MuiDialogTitle-root + .MuiDialogContent-root": {
            paddingTop: "24px",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            bgcolor: "#FDF9F3",
            boxShadow: "0 18px 60px rgba(44,37,30,0.14)",
            m: 0,
          },
        }}
        aria-labelledby="update-password-dialog-title"
      >
        <DialogTitle
          id="update-password-dialog-title"
          sx={{
            bgcolor: "#FDF9F3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            px: 3,
            py: 2,
            borderRadius: "16px 16px 0 0",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
            Update Password
          </Typography>

          <IconButton
            onClick={handleCloseUpdatePasswordModal}
            size="small"
            disabled={isUpdatingPassword}
            sx={{
              position: "absolute",
              right: 16,
              width: 28,
              height: 28,
              border: "1px solid rgba(44,37,30,0.06)",
              bgcolor: "#fff",
              color: "#2E2D2D",
              fontSize: "16px",
              "&:hover": { bgcolor: "#C5A572", color: "#FEFEFE" },
              "&:disabled": { opacity: 0.5 },
            }}
            aria-label="close"
          >
            ✕
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: "24px",
            px: 3,
            pb: 3,
            bgcolor: "#FEFEFE",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
            gap: 0,
            overflowX: "hidden",
            overflowY: "auto",
            "&.MuiDialogContent-root": {
              paddingTop: "24px",
            },
          }}
        >
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleUpdatePassword();
            }}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box>
              <label style={{ fontWeight: 500, marginBottom: 8, display: "block", color: "#000000" }}>
                New Password <span style={{ color: '#f44336' }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewPassword(value);
                    // Real-time validation
                    const error = validatePassword(value);
                    setPasswordErrors((prev) => ({ 
                      ...prev, 
                      newPassword: error,
                      // Also re-validate confirm password if it has a value
                      confirmPassword: confirmPassword ? validateConfirmPassword(confirmPassword, value) : prev.confirmPassword
                    }));
                  }}
                  onBlur={() => {
                    // Validate on blur if field has value
                    if (newPassword) {
                      const error = validatePassword(newPassword);
                      setPasswordErrors((prev) => ({ ...prev, newPassword: error }));
                    }
                  }}
                  style={{
                    padding: "10px 40px 10px 10px",
                    borderRadius: "6px",
                    border: passwordErrors.newPassword ? '1px solid #f44336' : '1px solid #C7A76C',
                    fontSize: 16,
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: '#fff'
                  }}
                  placeholder="Enter new password"
                  disabled={isUpdatingPassword}
                />
                {newPassword && (
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      color: "#2C251E",
                    }}
                  >
                    {showNewPassword ? <Visibility /> : <VisibilityOff />}
                  </button>
                )}
              </div>
              {passwordErrors.newPassword && (
                <Typography variant="caption" sx={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  mt: 0.5,
                  display: 'block'
                }}>
                  {passwordErrors.newPassword}
                </Typography>
              )}
            </Box>

            <Box>
              <label style={{ fontWeight: 500, marginBottom: 8, display: "block", color: "#000000" }}>
                Confirm New Password <span style={{ color: '#f44336' }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setConfirmPassword(value);
                    // Real-time validation
                    const error = validateConfirmPassword(value, newPassword);
                    setPasswordErrors((prev) => ({ ...prev, confirmPassword: error }));
                  }}
                  onBlur={() => {
                    // Validate on blur if field has value
                    if (confirmPassword) {
                      const error = validateConfirmPassword(confirmPassword, newPassword);
                      setPasswordErrors((prev) => ({ ...prev, confirmPassword: error }));
                    }
                  }}
                  style={{
                    padding: "10px 40px 10px 10px",
                    borderRadius: "6px",
                    border: passwordErrors.confirmPassword ? '1px solid #f44336' : '1px solid #C7A76C',
                    fontSize: 16,
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: '#fff'
                  }}
                  placeholder="Confirm new password"
                  disabled={isUpdatingPassword}
                />
                {confirmPassword && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      color: "#2C251E",
                    }}
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </button>
                )}
              </div>
              {passwordErrors.confirmPassword && (
                <Typography variant="caption" sx={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  mt: 0.5,
                  display: 'block'
                }}>
                  {passwordErrors.confirmPassword}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            bgcolor: "#FEFEFE",
            justifyContent: "center",
            borderRadius: "0 0 16px 16px",
          }}
        >
          <Button
            onClick={handleUpdatePassword}
            disabled={isUpdatingPassword}
            variant="contained"
            sx={{
              backgroundColor: isUpdatingPassword ? "#D3CBC1" : "rgb(197, 165, 114)",
              color: "#fff",
              padding: "10px 22px",
              fontSize: "15px",
              fontWeight: 600,
              width: "100%",
              textTransform: "none",
              boxShadow: "none",
              border: "1px solid rgb(197, 165, 114)",
              "&:hover": {
                backgroundColor: isUpdatingPassword ? "#D3CBC1" : "#8F7049",
                boxShadow: "none",
              },
              "&:disabled": {
                backgroundColor: "#D3CBC1",
                color: "#F5F1EC",
                borderColor: "#D3CBC1",
              },
            }}
          >
            {isUpdatingPassword ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                <span>Saving...</span>
              </Box>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideDownTransition}
        key={snackbarMessage}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          icon={false}
          sx={{
            backgroundColor:
              snackbarSeverity === "success" 
                ? "#558363" 
                : snackbarMessage.includes("Failed to update password")
                  ? "rgb(159, 66, 35)"
                  : "#D32F2F",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PersonnelPatientRecord;
